import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

export type SavedStatus = "saved" | "applied";

export interface SavedOpportunity {
  id: string;
  user_id: string;
  title: string;
  source: string | null;
  source_type: string | null;
  amount: string | null;
  deadline: string | null;
  url: string | null;
  description: string | null;
  matched_area: string | null;
  fingerprint: string;
  status: SavedStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SavableGrant {
  title: string;
  source?: string | null;
  source_type?: string | null;
  amount?: string | null;
  deadline?: string | null;
  url?: string | null;
  description?: string | null;
  matched_area?: string | null;
}

/** Fingerprint a grant for de-dup. Prefers url, falls back to title|source. */
export const fingerprintGrant = (grant: SavableGrant): string => {
  if (grant.url && grant.url.trim()) return `url:${grant.url.trim().toLowerCase()}`;
  return `ts:${(grant.title || "").trim().toLowerCase()}|${(grant.source || "").trim().toLowerCase()}`;
};

/**
 * Loads the current user's saved opportunities and exposes mutators.
 * Returns an empty map for unauthenticated users.
 */
export const useSavedOpportunities = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<SavedOpportunity[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("saved_opportunities")
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });
    if (!error) setItems((data ?? []) as SavedOpportunity[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const findByFingerprint = useCallback(
    (fp: string) => items.find((i) => i.fingerprint === fp) ?? null,
    [items],
  );

  const upsertStatus = useCallback(
    async (grant: SavableGrant, status: SavedStatus): Promise<boolean> => {
      if (!user) {
        toast.error("Sign in to save opportunities");
        return false;
      }
      const fp = fingerprintGrant(grant);
      const payload = {
        user_id: user.id,
        title: grant.title,
        source: grant.source ?? null,
        source_type: grant.source_type ?? null,
        amount: grant.amount ?? null,
        deadline: grant.deadline ?? null,
        url: grant.url ?? null,
        description: grant.description ?? null,
        matched_area: grant.matched_area ?? null,
        fingerprint: fp,
        status,
      };
      const { error } = await supabase
        .from("saved_opportunities")
        .upsert(payload, { onConflict: "user_id,fingerprint" });
      if (error) {
        toast.error("Could not update saved opportunity");
        return false;
      }
      trackEvent("grant_saved", {
        status,
        source: grant.source ?? null,
        source_type: grant.source_type ?? null,
        matched_area: grant.matched_area ?? null,
        has_url: Boolean(grant.url),
      });
      await refresh();
      return true;
    },
    [user, refresh],
  );

  const remove = useCallback(
    async (grant: SavableGrant): Promise<boolean> => {
      if (!user) return false;
      const fp = fingerprintGrant(grant);
      const { error } = await supabase
        .from("saved_opportunities")
        .delete()
        .eq("user_id", user.id)
        .eq("fingerprint", fp);
      if (error) {
        toast.error("Could not remove saved opportunity");
        return false;
      }
      trackEvent("grant_unsaved", {
        source: grant.source ?? null,
        source_type: grant.source_type ?? null,
      });
      await refresh();
      return true;
    },
    [user, refresh],
  );

  const removeById = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) return false;
      const { error } = await supabase
        .from("saved_opportunities")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);
      if (error) {
        toast.error("Could not remove saved opportunity");
        return false;
      }
      await refresh();
      return true;
    },
    [user, refresh],
  );

  return {
    items,
    loading,
    refresh,
    findByFingerprint,
    save: (g: SavableGrant) => upsertStatus(g, "saved"),
    markApplied: (g: SavableGrant) => upsertStatus(g, "applied"),
    remove,
    removeById,
  };
};
